import React, { useCallback, useEffect } from 'react';
import CircleCloseIcon from 'ui/Icons/circleClose.component.svg';
import MagnifyingIcon from 'ui/Icons/magnifying-glass.component.svg';
import Loading from 'ui/AsyncSearchDropdown/Loading';
import TextInput from 'pureUi/TextInput';
import classNames from 'classnames';
import { IDirectoryEntrySimpleSearchResult } from 'services/CrmApi/types/DirectoryEntry';
import { isNil } from 'lodash-es';
import { useCrmDirectoryEntry } from 'services/CrmApi/queries/useCrmDirectoryEntry';

const MAX_ITEMS_TO_SHOW = 10;

interface ISearchOptionsProps {
  directoryEntries: IDirectoryEntrySimpleSearchResult[];
  setSearchTerm: (value: string) => void;
  onChange: (value: string | null, directoryEntry?: IDirectoryEntrySimpleSearchResult) => void;
}

const SearchOptions = ({ directoryEntries, setSearchTerm, onChange }: ISearchOptionsProps) => {
  const searchOptionClassname = classNames(
    'p-[5px] my-[5px] flex flex-col justify-center font-pt-sans rounded-[3px] hover:bg-gray-10 cursor-pointer'
  );

  return (
    <div className="search-options">
      {directoryEntries.slice(0, MAX_ITEMS_TO_SHOW).map((option, index, arr) => {
        const key = `search-option-${option.id}`;
        const label = option.name;
        const handleSelectItem = () => {
          setSearchTerm(label);
          onChange(option.id, option);
        };
        return (
          <>
            <div key={key} className={searchOptionClassname} onClick={handleSelectItem}>
              <span>{label}</span>
            </div>
            {index + 1 < arr.length && <div className="h-[0px] border border-gray-10" />}
          </>
        );
      })}
    </div>
  );
};

interface IProps {
  className?: string;
  value: string;
  onChange: (value: string | null, directoryEntry?: IDirectoryEntrySimpleSearchResult) => void;
  excludeIds: string[];
  directoryEntries: IDirectoryEntrySimpleSearchResult[];
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  isFetching: boolean;
  isFetched: boolean;
  disabled?: boolean;
}

/**
 * You should not use this component directly
 * Instead, use ExistingDirectoryEntriesFullSearch or ExistingDirectoryEntriesSimpleSearch (or create a new one)
 */
export const InternalDirectoryEntriesSearch: React.FC<IProps> = ({
  className = 'relative w-full bg-white-hard flex flex-col',
  value,
  onChange,
  excludeIds,
  directoryEntries,
  searchTerm,
  setSearchTerm,
  isFetching,
  isFetched,
  disabled = false,
}) => {
  const { directoryEntry } = useCrmDirectoryEntry(value, { enabled: !!value });

  useEffect(() => {
    if (directoryEntry) {
      setSearchTerm(directoryEntry.fullName ?? '');
    }
  }, [directoryEntry, setSearchTerm]);

  const circleCloseIconClassname = classNames(
    'block cursor-pointer fill-brown-80 hover:fill-brown-120 absolute w-[18px] top-[10px] right-[10px]',
    { hidden: !value }
  );

  const filteredDirectoryEntries = directoryEntries.filter(directoryEntry => !excludeIds.some(id => id === directoryEntry.id));
  const selectedDirectoryEntry = filteredDirectoryEntries.find(item => item.id === value) ?? null;

  // Reset the searchTerm when Formik resets the value
  useEffect(() => {
    if (value === '' || isNil(value)) {
      setSearchTerm('');
    }
  }, [setSearchTerm, value]);

  const handleRemoveSearch = useCallback(() => {
    setSearchTerm('');
    onChange(null);
  }, [onChange, setSearchTerm]);

  const handleTextChange: React.FormEventHandler<HTMLInputElement> = useCallback(
    e => {
      setSearchTerm(e.currentTarget.value);
      onChange(null);
    },
    [onChange, setSearchTerm]
  );

  return (
    <div className={className}>
      <TextInput
        onChange={disabled ? undefined : handleTextChange}
        placeholder="Search from directory ..."
        className="flex flex-col relative min-h-[39px] max-h-[39px]"
        value={searchTerm}
        inputClassName={classNames('!py-[10px] !px-[30px] !text-black', { '!bg-gray-10': disabled, '!bg-white-hard': !disabled })}
        disabled={disabled}
      >
        {!disabled && <CircleCloseIcon onClick={handleRemoveSearch} className={circleCloseIconClassname} />}
        <MagnifyingIcon className="absolute w-[18px] top-[10px] left-[10px] fill-light-gray rotate-90" />

        {!disabled && searchTerm?.length >= 3 ? (
          <div className="directory-entries-search-options absolute left-[0px] w-full top-[41px] bg-white-hard px-[10px] z-[9]">
            {isFetching ? (
              <div className="py-2">
                <Loading />
              </div>
            ) : filteredDirectoryEntries.length && !value ? (
              <div className="flex flex-col flex-1 max-h-[295px] overflow-auto border-b border-gray-10">
                <SearchOptions directoryEntries={filteredDirectoryEntries} setSearchTerm={setSearchTerm} onChange={onChange} />
              </div>
            ) : (
              !selectedDirectoryEntry &&
              isFetched && (
                <div className="text-black text-[15px] font-normal mt-[10px] italic font-pt-sans">
                  No results found.
                </div>
              )
            )}
          </div>
        ) : (
          <span></span>
        )}
      </TextInput>
    </div>
  );
};
